import React, { useState, useCallback, useEffect, useRef } from 'react';
import GoogleMapReact from 'google-map-react';
import { translate } from '../Services/translation';
import StoreLocatorMarker from './StoreLocatorMarker';
import SvgIcon from '../Components/primitives/SvgIcon';
import useScrollable from '../Hooks/useScrollable';
import { cn } from '../Utils/cn';

export const DEFAULT_MAP_CENTER = {
  lat: 64.469349,
  lng: 11.540807,
};

export const MAP_COMPONENT_RESTRICTIONS = {
  types: ['(regions)'],
  componentRestrictions: {
    country: window?.__litium?.constants?.currentCountry || "NO"
  },
};

const StoreLocator = (props) => {
  const { stores } = props;

  // List stores for map
  const [mapStores, setMapStores] = useState(stores);
  //List stores for sidebar
  const [listStores, setListStores] = useState(stores);

  const [selectedOption, setSelectedOption] = useState("filterDealer");
  const [selectedView, setSelectedView] = useState("list");

  const [customerLocation, setCustomerLocation] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [isDealer, setIsDealer] = useState(false);
  const [isCabin, setIsCabin] = useState(false);

  const [activeMarkerIndex, setActiveMarkerIndex] = useState(-1);

  const [mapReference, setMapReference] = useState(null);
  const [mapsReference, setMapsReference] = useState(null);

  const searchInput = useRef(null);
  const searchBox = useRef(null);

  useEffect(() => {
    let tempStores = mapStores;

    // Nearest filter
    if (customerLocation && mapReference && mapsReference) {
      let nearestStores = [];
      tempStores.forEach((store) => {
        const storeLocation = new mapsReference.LatLng(store.lat, store.lng);
        nearestStores.push({
          index: store.index,
          distance: mapsReference.geometry.spherical.computeDistanceBetween(
            customerLocation,
            storeLocation
          ),
          lat: store.lat,
          lng: store.lng,
        });
      });
      nearestStores.sort((a, b) => {
        return a.distance - b.distance;
      });
      nearestStores = nearestStores.slice(0, 4);

      const nearestStoresIndexes = nearestStores.map((store) => store.index);
      tempStores = tempStores.filter((store) =>
        nearestStoresIndexes.includes(store.index)
      );
      tempStores.sort(
        (a, b) =>
          nearestStoresIndexes.indexOf(a.index) -
          nearestStoresIndexes.indexOf(b.index)
      );

      const bounds = new mapsReference.LatLngBounds();
      bounds.extend(customerLocation);
      for (let i = 0; i < tempStores.length; i++) {
        const position = new mapsReference.LatLng(
          tempStores[i].lat,
          tempStores[i].lng
        );
        bounds.extend(position);
      }
      mapReference.fitBounds(bounds);
    } else if (customerLocation === null && mapReference && mapsReference) {
      const osloLocation = new mapsReference.LatLng(59.911491, 10.757933);
      let nearestStores = [];
      tempStores.forEach((store) => {
        const storeLocation = new mapsReference.LatLng(store.lat, store.lng);
        nearestStores.push({
          index: store.index,
          distance: mapsReference.geometry.spherical.computeDistanceBetween(
            osloLocation,
            storeLocation
          ),
          lat: store.lat,
          lng: store.lng,
        });
      });
      nearestStores.sort((a, b) => {
        return a.distance - b.distance;
      });
      const nearestStoresIndexes = nearestStores.map((store) => store.index);
      tempStores = tempStores.filter((store) =>
        nearestStoresIndexes.includes(store.index)
      );
      tempStores.sort(
        (a, b) =>
          nearestStoresIndexes.indexOf(a.index) -
          nearestStoresIndexes.indexOf(b.index)
      );

      const bounds = new mapsReference.LatLngBounds();
      bounds.extend(osloLocation);
      for (let i = 0; i < tempStores.length; i++) {
        const position = new mapsReference.LatLng(
          tempStores[i].lat,
          tempStores[i].lng
        );
        bounds.extend(position);
      }
      mapReference.fitBounds(bounds);
    }

    setListStores(tempStores);
  }, [mapStores, setListStores, customerLocation, mapReference, mapsReference]);

  useEffect(() => {
    let tempStores = stores;
    if (isDealer != isCabin) {
      tempStores = stores.filter((store) => {
        if (isDealer) {
          return store.isDealer;
        } else if (isCabin) {
          return store.isCabin;
        }
        return false;
      });
    }
    setMapStores(tempStores);
  }, [stores, isDealer, isCabin, setMapStores]);

  let retryCount = 0; // search with exact word (adding comma) first, if no result then retry (with auto completion)
  const onPlaceChange = () => {
    const place = searchBox.current.getPlace();
    if (place.geometry) {
      const location = new mapsReference.LatLng(
        place.geometry.location.lat(),
        place.geometry.location.lng()
      );
      mapReference.panTo(location);
      setCustomerLocation(location);
    } else if (!place.geometry && place.name) {
      const autocompleteService = new mapsReference.places.AutocompleteService();
      const placesService = new mapsReference.places.PlacesService(mapReference);
      autocompleteService.getPlacePredictions(
        {
          input: place.name + (retryCount == 0 ? ',' : ''),
          ...MAP_COMPONENT_RESTRICTIONS,
        },
        (predictions, status) => {
          if (
            status == mapsReference.places.PlacesServiceStatus.OK &&
            predictions &&
            predictions[0] &&
            predictions[0].reference
          ) {
            placesService.getDetails(
              {
                reference: predictions[0].reference,
              },
              (details, status) => {
                if (
                  status == mapsReference.places.PlacesServiceStatus.OK &&
                  details &&
                  details.geometry
                ) {
                  retryCount = 0;
                  const location = new mapsReference.LatLng(
                    details.geometry.location.lat(),
                    details.geometry.location.lng()
                  );
                  mapReference.panTo(location);
                  setCustomerLocation(location);
                  setSearchQuery(predictions[0].description);
                } else {
                  setCustomerLocation(null);
                }
              }
            );
          } else {
            retryCount++;
            if (retryCount > 1) {
              retryCount = 0;
              setCustomerLocation(null);
            } else {
              onPlaceChange();
            }
          }
        }
      );
    } else {
      setCustomerLocation(null);
    }
  };

  const handleOnPlacesChanged = useCallback(
    () => onPlaceChange(),
    [searchBox, mapReference, mapsReference, setCustomerLocation, setSearchQuery]
  );

  useEffect(() => {
    if (!searchBox.current && mapsReference) {
      searchBox.current = new mapsReference.places.Autocomplete(
        searchInput.current,
        {
          ...MAP_COMPONENT_RESTRICTIONS,
        }
      );
      searchBox.current.addListener('place_changed', handleOnPlacesChanged);
    }

    return () => {
      if (mapsReference) {
        searchBox.current = null;
        mapsReference.event.clearInstanceListeners(searchBox);
      }
    };
  }, [searchInput, searchBox, mapsReference, handleOnPlacesChanged]);

    const {
    ref: listRef,
    isScrollable,
    isAtTop,
    isAtBottom,
  } = useScrollable([listStores]);

//   const handlePlaceSelect = useCallback(async (place) => { console.log(place)
//   if (place && place.geometry && place.geometry.location) {
//     const location = new mapsReference.LatLng(
//       place.geometry.location.lat(),
//       place.geometry.location.lng()
//     );
    
//     // Update the map view
//     mapReference.panTo(location);
    
//     // Update the customer location which will trigger store filtering
//     setCustomerLocation(location);
    
//     // Update the search query with the formatted address if available
//     if (place.formatted_address) {
//       setSearchQuery(place.formatted_address);
//     } else if (place.name) {
//       setSearchQuery(place.name);
//     }
    
//     // Reset active marker when searching
//     setActiveMarkerIndex(-1);
//   }
// }, [mapsReference, mapReference, setCustomerLocation, setSearchQuery, setActiveMarkerIndex]);

// useEffect(() => {
//   if (!mapsReference || !searchInput.current) return;

//   // Create a container for the autocomplete
//   const container = document.createElement('div');
//   container.style.position = 'fixed';
//   container.style.opacity = '0';
//   container.style.pointerEvents = 'none';
//   document.body.appendChild(container);

//   // Create the autocomplete element with minimal configuration
//   const autocomplete = new mapsReference.places.PlaceAutocompleteElement();
//   container.appendChild(autocomplete);

//   // Set country restriction if available
//   if (MAP_COMPONENT_RESTRICTIONS?.componentRestrictions?.country) {
//     autocomplete.country = MAP_COMPONENT_RESTRICTIONS.componentRestrictions.country;
//   }

//   // Sync the input value with the autocomplete
//   const syncInput = () => {
//     if (searchInput.current) {
//       autocomplete.value = searchInput.current.value;
//     }
//   };

//   // Handle place selection
//   const onPlaceSelect = async (e) => {
//     e.stopPropagation();
//     try {
//       const place = await autocomplete.getPlace();
//       if (place) {
//         handlePlaceSelect(place);
//       }
//     } catch (error) {
//       console.error('Error getting place details:', error);
//     }
//   };

//   // Add event listeners
//   searchInput.current.addEventListener('input', syncInput);
//   autocomplete.addEventListener('place_select', onPlaceSelect);

//   // Initial sync
//   syncInput();

//   // Store the autocomplete instance for cleanup
//   searchBox.current = { autocomplete, container };

//   // Cleanup
//   return () => {
//     if (searchInput.current) {
//       searchInput.current.removeEventListener('input', syncInput);
//     }
//     autocomplete.removeEventListener('place_select', onPlaceSelect);
//     if (container.parentNode) {
//       container.parentNode.removeChild(container);
//     }
//   };
// }, [mapsReference, handlePlaceSelect]);


  const handleDealerClick = () => setIsDealer(!isDealer);
  const handleCabinClick = () => setIsCabin(!isCabin);
  const searchInputOnChange = (e) => setSearchQuery(e.target.value);

  /* Map */
  const onShowOnMapClick = (e, index) => {
    onMapChildClick(index);
    e.preventDefault();
  };

  const onMarkerClose = () => {
    setActiveMarkerIndex(-1);
  };

  const onDealerSelect = () => {
    const nextButton =
        document.querySelector('.checkout__step.active .checkout__step-content-next button');

    nextButton.click();
  }

  const onMapChildClick = (index) => {
    const store = mapStores.find((store) => store.index == index);
    if (store) {
      mapReference.panTo({
        lat: store.lat,
        lng: store.lng,
      });
      const mapDivHeight = mapReference.getDiv().offsetHeight;
      if (mapDivHeight < 400) {
        mapReference.panBy(0, -mapDivHeight / 3);
      }
    }
    setActiveMarkerIndex(index);
  };

  const onGoogleApiLoaded = ({ map, maps }) => {
    setMapReference(map);
    setMapsReference(maps);

    const bounds = new maps.LatLngBounds();
    for (let i = 0; i < mapStores.length; i++) {
      const position = new maps.LatLng(mapStores[i].lat, mapStores[i].lng);
      bounds.extend(position);
    }
    map.fitBounds(bounds); // Automatically center the map fitting all markers on the screen
  };

  const createMapOptions = (maps) => {
    return {
      streetViewControl: true,
      clickableIcons: false,
      styles: [
        {
          stylers: [
            {
              visibility: 'on',
            },
          ],
        },
      ],
    };
  };

    const options = [
    { label: translate('storelocator.dealer'), value: "filterDealer" },
    { label: translate('storelocator.cabin'), value: "filterCabin" },
  ];

  const viewOptions = [
    { label: translate('storelocator.list'), value: "list" },
    { label: translate('storelocator.map'), value: "map" },
  ];

  const activeIndex = options.findIndex((opt) => opt.value === selectedOption);
  const activeViewIndex = viewOptions.findIndex((opt) => opt.value === selectedView);

  const handleChange = (value) => {
    setSelectedOption(value);

    const stateMap = {
      filterDealer: { isDealer: true, isCabin: false },
      filterCabin: { isDealer: false, isCabin: true },
      showAll: { isDealer: false, isCabin: false }
    };

    const newState = stateMap[value];
    if (!newState) return;

    setIsDealer(newState.isDealer);
    setIsCabin(newState.isCabin);
  };

  const handleSearchClear = () => {
    setSearchQuery('');
    setListStores(stores);
    setMapStores(stores);
    setActiveMarkerIndex(-1);
    searchInput.current.value = '';
    searchInput.current.focus();

    if (mapReference && mapsReference) {
      onGoogleApiLoaded({ map: mapReference, maps: mapsReference });
    }
  };

  return (
    <div className='space-y-4'>
      <div class="border border-border-primary bg-white p-4 lg:p-8 rounded-2xl space-y-4">
        <div className="store-locator-component grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-4 lg:gap-8">
          <div className="store-locator-search-column lg:space-y-6">
            <div className="search-action-wrapper space-y-4">
              <label className='flex gap-2 items-center py-2 pl-6 pr-2 rounded-2xl bg-input-primary min-h-12 focus-within:outline-2 focus-within:outline-blue-500'>
                <input
                  ref={searchInput}
                  className="text-base text-input-secondary w-full outline-none"
                  type="text"
                  placeholder={translate('general.search')}
                  onChange={searchInputOnChange}
                  value={searchQuery}
                />
                <div>
                  {searchQuery && (
                    <button type='button' onClick={handleSearchClear} className='flex'>
                      <span className='icon icon-outlined text-xl!'>close</span>
                    </button>
                  )}
                </div>
              </label>

              <div className="relative grid grid-cols-2 bg-grey-lighter rounded-full overflow-hidden">
                <div
                  className="absolute h-full bg-accent rounded-full transition-all duration-300 ease-out top-0"
                  style={{
                    width: `calc(${100 / options.length}%)`,
                    transform: `translateX(${100 * activeIndex}%)`
                  }}
                />

            {options.map(({label, value}) => {
              const isActive = value === selectedOption;
              return (
                <button
                  key={value}
                  onClick={() => handleChange(value)}
                  className={`relative z-10 px-4 py-5 lg:p-6 rounded-full font-semibold text-base flex items-center justify-center gap-2 transition-colors duration-300 ease-out truncate ${
                    isActive
                      ? "text-white"
                      : "text-text-secondary hover:text-text-secondary/80"
                  }`}
                >
                  {value === 'filterCabin' && <SvgIcon.cabinCenter />}
                  {value === 'filterDealer' && <SvgIcon.dealer />}
                  {label}
                </button>
              );
            })}
          </div>

            </div>
            <div className="search-result-wrapper hidden lg:block relative">
                <ul className={cn(
                  "search-results space-y-2",
                  isScrollable && !isAtTop && 'before:absolute before:top-0 before:left-0 before:right-0 before:h-12 before:bg-gradient-to-b before:from-white before:to-transparent before:pointer-events-none',
                  isScrollable && !isAtBottom && 'after:absolute after:bottom-0 after:left-0 after:right-0 after:h-12 after:bg-gradient-to-t after:from-white after:to-transparent after:pointer-events-none',
                  isScrollable && 'pt-6 pb-6'
                )} 
                ref={listRef}>
                {listStores.map((store) => (
                  <li key={store.index} className="p-4 border border-border-primary rounded-2xl lg:px-6">
                    <div className="store-name text-base font-extrabold text-text-secondary">{store.name}</div>
                    <div className="store-company text-text-secondary text-sm">{store.company}</div>
                    <div className="store-address text-text-secondary text-sm">{store.address}</div>
                    <div className="store-links">
                      <a
                        className="store-link text-sm text-accent underline hover:no-underline"
                        href={store.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {translate('storelocator.visitlink')}
                      </a>
                      <span className="separator"></span>
                      <a
                        className="store-map-link text-sm text-accent underline hover:no-underline cursor-pointer"
                        onClick={(e) => onShowOnMapClick(e, store.index)}
                      >
                        {translate('storelocator.showonmap')}
                      </a>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="store-locator-map-column min-h-[384px] lg:min-h-[668px] hidden lg:block">
            <GoogleMapReact
              bootstrapURLKeys={{
                key: window.__litium.constants.googleMapApiKey,
                libraries: ['places', 'geometry'],
              }}
              defaultCenter={DEFAULT_MAP_CENTER}
              defaultZoom={6}
              onChildClick={onMapChildClick}
              onGoogleApiLoaded={onGoogleApiLoaded}
              yesIWantToUseGoogleMapApiInternals={true}
              options={createMapOptions}
            >
              {mapStores.map((store) => (
                <StoreLocatorMarker
                  key={store.index}
                  lat={store.lat}
                  lng={store.lng}
                  store={store}
                  show={store.index == activeMarkerIndex}
                  mapsReference={mapsReference}
                  onClose={onMarkerClose}
                  onDealerSelect={onDealerSelect}
                  hideDealerSelect
                />
              ))}
            </GoogleMapReact>
          </div>
        </div>
      </div>

      <div className='border rounded-radius border-border-primary bg-white p-4 flex flex-col gap-4 lg:hidden store-locator-component'>
        <div className="relative grid grid-cols-2 bg-grey-lighter rounded-full overflow-hidden">
              <div
                className="absolute h-full bg-accent rounded-full transition-all duration-300 ease-out top-0"
                style={{
                  width: `calc(${100 / viewOptions.length}%)`,
                  transform: `translateX(${100 * activeViewIndex}%)`
                }}
              />

          {viewOptions.map(({label, value}) => {
            const isActive = value === selectedView;
            return (
              <button
                key={value}
                onClick={() => setSelectedView(value)}
                className={`relative z-10 px-4 py-5 lg:p-6 rounded-full font-semibold text-base gap-2 transition-colors duration-300 ease-out truncate ${
                  isActive
                    ? "text-white"
                    : "text-text-secondary hover:text-text-secondary/80"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>

        <div className="w-full h-[600px] overflow-hidden">
          <div 
            className={`grid grid-cols-2 w-[200%] h-full transition-transform duration-300 ease-in-out ${
              selectedView === 'list' ? 'translate-x-[-50%]' : 'translate-x-0'
            }`}
          >
            {/* Map View - Second Column */}
            <div className="w-full h-full">
              <GoogleMapReact
                bootstrapURLKeys={{
                  key: window.__litium.constants.googleMapApiKey,
                  libraries: ['places', 'geometry'],
                }}
                defaultCenter={DEFAULT_MAP_CENTER}
                defaultZoom={6}
                onChildClick={onMapChildClick}
                onGoogleApiLoaded={onGoogleApiLoaded}
                yesIWantToUseGoogleMapApiInternals={true}
                options={createMapOptions}
              >
                {mapStores.map((store) => (
                  <StoreLocatorMarker
                    key={store.index}
                    lat={store.lat}
                    lng={store.lng}
                    store={store}
                    show={store.index == activeMarkerIndex}
                    mapsReference={mapsReference}
                    onClose={onMarkerClose}
                    onDealerSelect={onDealerSelect}
                    hideDealerSelect
                  />
                ))}
              </GoogleMapReact>
            </div>

            {/* List View */}
            <div className="w-full h-full bg-white overflow-y-auto">
              <ul className="search-results space-y-2">
                {listStores.map((store) => (
                  <li key={store.index + 'mobile'} className="p-4 border border-border-primary rounded-2xl lg:px-6">
                    <div className="store-name text-base font-extrabold text-text-secondary">{store.name}</div>
                    <div className="store-company text-text-secondary text-sm">{store.company}</div>
                    <div className="store-address text-text-secondary text-sm">{store.address}</div>
                    <div className="store-links">
                    <a
                      className="store-link text-sm text-accent underline hover:no-underline"
                      href={store.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {translate('storelocator.visitlink')}
                    </a>
                    <span className="separator"></span>
                    <a
                      className="store-map-link text-sm text-accent underline hover:no-underline cursor-pointer"
                      onClick={(e) => onShowOnMapClick(e, store.index)}
                    >
                      {translate('storelocator.showonmap')}
                    </a>
                  </div>
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </div>

      </div>
    </div>
  );
};

export default StoreLocator;
